import * as React from 'react'
import { trackPageScrollbar } from '@/lib/page-scrollbar'
import { useSessionUser } from '@/hooks/use-session'
import { versionDriftRedirect } from '@/lib/app-version'
import appCss from '../app.css?url'
import {
  ClientOnly,
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  defaultStringifySearch,
  redirect,
  retainSearchParams,
  stripSearchParams,
  useNavigate,
  useRouter,
  useRouterState,
} from '@tanstack/react-router'
import { noop, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/sonner'
import * as v from 'valibot'
import { FiltersShape, Profile } from '@/lib/profile-search-schema'
import { useAppStore } from '@/lib/app-store'
import { accentThemeVars } from '@/lib/accent-theme'
import { setCookie, deleteCookie } from '@/lib/cookies'
import { CookieBanner } from './_app/-components/shared/cookie-banner'
import { ModalSlotOutlet } from '@/components/custom/modal-slot'
import { type I18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { useLingui } from '@lingui/react/macro'
import { FormOptionalLabelProvider } from '@/components/custom/form/contexts'
import { resolveServerTheme } from '@/lib/server-theme'
import { resolveRadarStyle } from '@/lib/radar-style'
import { isLightOnlyPath, LIGHT_ONLY_PUBLIC_PATHS } from '@/lib/light-only-routes'
import { splitLocalePath } from '@/lib/locale-path'
import { HYDRATION_REWRITE_MONITOR_SCRIPT } from '@/lib/hydration-rewrite-monitor'
import { assetUrl, getRuntimeCdnUrl } from '@/lib/asset-url'
import { getRuntimeImgUrl } from '@/lib/sized-image'
import { CATALOG_BOOTSTRAP_SCRIPT, preloadRouteCatalogs } from '@/lib/i18n-catalogs'
import type { Locale } from 'shared/locales'
import type { Customize } from 'shared/types'
import { persistedCacheIdentity } from '@/main-helpers'
import { trpc, adoptCacheIdentity, clearQueryClient, OWNS_PERSISTED_CACHE, PERSISTED_IDENTITY_KEY } from '@/main'

// Mounted statically with the shell (sonner is ~9 KB gz in the entry): a toast must be interactive the moment the
// page is, not after an on-demand chunk fetch.
function AppToaster() {
  return (
    <Toaster
      className="whitespace-pre-line print:hidden"
      offset={{ top: 'calc(32px + env(safe-area-inset-top))' }}
      mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top))' }}
      position="top-center"
      theme="light"
    />
  )
}

export const defaultValues = {
  q: '',
  // The detail pane's OWN search, separate from `q`: two search boxes are visible at once on those screens.
  panelQ: '',
  p: 0,
  teamsSort: 'name',
  pageType: 'detailed',
  reportSort: 'index',
  distSort: 'index',
  distPosition: 'highs',
  boardType: 'competences',
  boardSort: 'index',
  boardAxis: 'x',
  orgType: 'basic',
  selected: 'first',
  type: 'competences',
  // TODO: this should not be necessary, check
  evaluation: 'owns',
  chatHistory: false,
  chatSize: 'compact',
} as const

const EvaluationV = v.picklist(['owns', 'ownsManual', 'team', 'manual'])

const rootSearchSchema = v.object({
  'q': v.optional(v.string(), defaultValues.q),
  'panelQ': v.optional(v.string(), defaultValues.panelQ),
  'p': v.optional(v.pipe(v.number(), v.minValue(0)), defaultValues.p),
  'o': v.fallback(v.optional(v.picklist(['adjust', 'global'])), undefined),
  'od': v.fallback(v.optional(v.picklist(['asc', 'desc'])), undefined),
  'offerSort': v.fallback(v.optional(v.picklist(['recent', 'applicants'])), undefined),
  'jobSort': v.fallback(v.optional(v.picklist(['recent', 'applicants'])), undefined),
  'teamsSort': v.fallback(v.optional(v.picklist(['name', 'size']), defaultValues.teamsSort), defaultValues.teamsSort),
  'pageType': v.fallback(
    v.optional(v.picklist(['detailed', 'resume']), defaultValues['pageType']),
    defaultValues['pageType'],
  ),
  'reportSort': v.fallback(
    v.optional(v.picklist(['index', 'desc']), defaultValues['reportSort']),
    defaultValues['reportSort'],
  ),
  'distSort': v.fallback(
    v.optional(v.picklist(['desc', 'index']), defaultValues['distSort']),
    defaultValues['distSort'],
  ),
  'distPosition': v.fallback(
    v.optional(v.picklist(['highs', /* 'medium', */ 'lows']), defaultValues['distPosition']),
    defaultValues['distPosition'],
  ),
  'boardType': v.fallback(
    v.optional(v.picklist(['competences', 'vectors', 'dimensions']), defaultValues['boardType']),
    defaultValues['boardType'],
  ),
  'boardSort': v.fallback(
    v.optional(v.picklist(['index', 'desc', 'asc']), defaultValues['boardSort']),
    defaultValues['boardSort'],
  ),
  'boardId': v.optional(v.union([v.number(), v.string()])), // competence | vector | dimension | member
  'boardAxis': v.fallback(v.optional(v.picklist(['x', 'y']), defaultValues['boardAxis']), defaultValues['boardAxis']),
  'boardPosition': v.optional(v.number()),
  'orgType': v.fallback(
    v.optional(v.picklist(['basic', 'professional', 'transversal', 'directives']), defaultValues['orgType']),
    defaultValues['orgType'],
  ),
  'first': v.optional(Profile),
  'second': v.optional(Profile),
  'selected': v.fallback(
    v.optional(v.picklist(['first', 'second', 'compare']), defaultValues['selected']),
    defaultValues['selected'],
  ),
  // Additive and independent of `selected`/`first`/`second` (not in REPORT_INPUT_KEYS), so it never alters the
  // report query.
  'collective': v.fallback(
    v.optional(v.picklist(['all', 'employees', 'jobs', 'teams', 'offers', 'candidates'])),
    undefined,
  ),
  // Kept in its OWN search key so the collective card's Role/Demographics refinements stay independent of the
  // compared profiles' filters; cleared whenever the active collective changes or closes.
  // The bounds are TAKEN from `FiltersShape`: without them a hand-edited URL parses here and is then rejected by the
  // tRPC input schema, which the card surfaces as a silent "No profiles found".
  'collectiveFilters': v.optional(
    v.object({
      level: FiltersShape.level,
      function: FiltersShape.function,
      sector: FiltersShape.sector,
      peopleInCharge: FiltersShape.peopleInCharge,
      sex: FiltersShape.sex,
      ageMin: FiltersShape.ageMin,
      ageMax: FiltersShape.ageMax,
      educationalLevel: FiltersShape.educationalLevel,
    }),
  ),
  'type': v.fallback(
    v.optional(v.picklist(['competences', 'values', 'personality']), defaultValues.type),
    defaultValues.type,
  ),
  // TODO: this should not be necessary, check
  'teamId': v.optional(v.number()),
  'evaluation': v.fallback(v.optional(EvaluationV), undefined),
  'members': v.optional(v.array(v.number())),
  'name': v.nullish(v.string()),
  // The modal slot: the path of the dialog route open over the page (`?@modal=/jobs/5/edit`), matched against
  // `routes/@modal/` by components/custom/modal-slot.tsx — docs/notes/app/dialog-routes.md.
  '@modal': v.fallback(v.optional(v.string()), undefined),
  // The assistant's conversation (a thread id; absent = a new one, not asked yet), whether its
  // history is showing, and the floating dock's size. Retained below, since the conversation follows every page —
  // docs/notes/app/assistant-panel.md.
  'chat': v.fallback(v.optional(v.pipe(v.string(), v.maxLength(64))), undefined),
  'chatHistory': v.fallback(v.optional(v.boolean(), defaultValues.chatHistory), defaultValues.chatHistory),
  'chatSize': v.fallback(v.optional(v.picklist(['compact', 'large']), defaultValues.chatSize), defaultValues.chatSize),
  // The public API hands out links like `/report?token=…&lang=…`; the token IS the session (sent as the Bearer header,
  // see main.tsx). Kept at the root + retained below so in-app navigation never strips the token out of the flow.
  'token': v.optional(v.string()),
  'lang': v.optional(v.string()),
  'clientId': v.optional(v.string()),
  'ref': v.optional(v.string()),
})

// Routes that retain all search params need this as the explicit type argument to `retainSearchParams<RootSearch>`:
// without it the generic falls back to `object`, which doesn't satisfy the route's search type.
export type RootSearch = v.InferOutput<typeof rootSearchSchema>

// Public per-deployment config from SST runtime secrets, which exist only server-side — the CI build (and so
// import.meta.env / Vite `define`) never sees them — hence process.env at SSR, baked in here. `process` is undefined
// in the browser, so the bundle ships the empty fallbacks and the `|| value` form makes the client a no-op clobber.
// USER/APP stay null by design: the SPA shell is auth-unaware and cacheable, so the user resolves client-side.
const publicEnv =
  typeof process !== 'undefined'
    ? {
        GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID ?? '',
        FACEBOOK_CLIENT_ID: process.env.FACEBOOK_CLIENT_ID ?? '',
        LINKEDIN_CLIENT_ID: process.env.LINKEDIN_CLIENT_ID ?? '',
        STRIPE_PUBLIC_KEY: process.env.STRIPE_PUBLIC_KEY ?? '',
        APP_ENV: process.env.APP_ENV ?? '',
        GA_MEASUREMENT_ID: process.env.GA_MEASUREMENT_ID ?? '',
        APP_VERSION: process.env.APP_VERSION ?? '',
        // Gates the client session-timeline capture (lib/session-actions); error reporting + breadcrumbs run regardless.
        WIDE_EVENTS_ENABLED: process.env.WIDE_EVENTS_ENABLED ?? '',
      }
    : null

const initGlobalsScript = [
  `window.GOOGLE_CLIENT_ID = window.GOOGLE_CLIENT_ID || ${JSON.stringify(publicEnv?.GOOGLE_CLIENT_ID ?? '')};`,
  `window.FACEBOOK_CLIENT_ID = window.FACEBOOK_CLIENT_ID || ${JSON.stringify(publicEnv?.FACEBOOK_CLIENT_ID ?? '')};`,
  `window.LINKEDIN_CLIENT_ID = window.LINKEDIN_CLIENT_ID || ${JSON.stringify(publicEnv?.LINKEDIN_CLIENT_ID ?? '')};`,
  `window.STRIPE_PUBLIC_KEY = window.STRIPE_PUBLIC_KEY || ${JSON.stringify(publicEnv?.STRIPE_PUBLIC_KEY ?? '')};`,
  `window.APP_ENV = window.APP_ENV || ${JSON.stringify(publicEnv?.APP_ENV ?? '')};`,
  `window.GA_MEASUREMENT_ID = window.GA_MEASUREMENT_ID || ${JSON.stringify(publicEnv?.GA_MEASUREMENT_ID ?? '')};`,
  `window.APP_VERSION = window.APP_VERSION || ${JSON.stringify(publicEnv?.APP_VERSION ?? '')};`,
  `window.WIDE_EVENTS_ENABLED = window.WIDE_EVENTS_ENABLED || ${JSON.stringify(publicEnv?.WIDE_EVENTS_ENABLED ?? '')};`,
  // `globalThis.__assetUrl` is the runtime resolver Vite's renderBuiltUrl form calls from JS chunks (vite.config.ts,
  // mirrored by lib/asset-url.ts). MUST be defined here, before <Scripts/>, so the first chunk's URLs already resolve.
  `window.__CDN_URL = window.__CDN_URL || ${JSON.stringify(getRuntimeCdnUrl())};`,
  `window.__IMG_URL = window.__IMG_URL || ${JSON.stringify(getRuntimeImgUrl())};`,
  `globalThis.__assetUrl = globalThis.__assetUrl || function(f){var b=(window.__CDN_URL||'').replace(/\\/$/,'');return (b?b+'/':'/')+String(f).replace(/^\\//,'')};`,
  // The route catalogs' loader, which every built chunk that owns one awaits first (lib/i18n-catalogs.ts).
  CATALOG_BOOTSTRAP_SCRIPT,
].join('')

// The render's trace, read off the store apps/app/src/server.ts runs it in (server/wide-events ssr-trace.ts publishes
// it on globalThis), so this isomorphic module never imports node:async_hooks. Empty on the client, where the guard
// below keeps the values the document was served with; the page's calls then continue the render's trace.
function ssrTrace(): { traceId: string; rootSpanId: string } {
  if (typeof window !== 'undefined') return { traceId: '', rootSpanId: '' }
  const store = (globalThis as { __ssrTraceAls?: { getStore(): { traceId: string; rootSpanId: string } | undefined } })
    .__ssrTraceAls
  return store?.getStore() ?? { traceId: '', rootSpanId: '' }
}

const traceInitScript = (trace: { traceId: string; rootSpanId: string }): string =>
  [
    `window.__TRACE_ID = window.__TRACE_ID || ${JSON.stringify(trace.traceId)};`,
    `window.__ROOT_SPAN_ID = window.__ROOT_SPAN_ID || ${JSON.stringify(trace.rootSpanId)};`,
  ].join('')

// Anything but an explicit 'dark'/'light' — no cookie, or a stale 'theme=undefined' — means follow the OS preference;
// treating a non-light value as "not system" strips the class for system-dark visitors and flashes light→dark.
// Light-only pages (the new landing) skip the class whatever the cookie or the OS say; see light-only-routes.ts.
const manageThemeScript = `
function manageTheme() {
  var m = document.cookie.match(/(?:^|;\\s*)theme=([^;]*)/)
  var theme = m ? m[1] : ''
  var lightOnly = ${JSON.stringify(LIGHT_ONLY_PUBLIC_PATHS)}.indexOf(location.pathname.replace(/\\/+$/, '') || '/') !== -1
  var dark = !lightOnly && (theme === 'dark' || (theme !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches))
  if (dark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}
manageTheme()
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', function () {
  var mode = document.documentElement.getAttribute('data-theme-mode')
  if (mode === 'dark' || mode === 'light') return
  manageTheme()
})
`

// The render-critical font + favicon live in public/, so they bypass Vite and go through assetUrl(), which reads the
// same runtime CDN host as every other asset — SSR and the first client render then produce identical markup.
// The preconnect overlaps the CDN handshake with HTML parsing; cdnOrigin is '' for same-origin assets (dev / e2e).

/**
 * The dialog params develop shipped before the modal slot (`?describeJob=` is what the public API's describe link
 * still emits). A URL carrying one is redirected to its `?@modal=` path, once, so old links keep opening.
 */
const LEGACY_MODAL_KEYS = ['editJob', 'editOffer', 'editEmployee', 'editTeam', 'describeJob', 'describeOnly']
const INDICATORS = new Set(['competences', 'values', 'personality'])

const legacyId = (value: unknown) =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : undefined

function legacyModalPath(search: Record<string, unknown>): string | undefined {
  for (const [key, section] of [
    ['editJob', 'jobs'],
    ['editOffer', 'offers'],
    ['editEmployee', 'employees'],
    ['editTeam', 'teams'],
  ] as const) {
    if (search[key] === 'new') return `/${section}/new`
    const edited = legacyId(search[key])
    if (edited) return `/${section}/${edited}/edit`
  }
  const described = legacyId(search.describeJob)
  if (!described) return undefined
  const only = INDICATORS.has(search.describeOnly as string) ? `/${String(search.describeOnly)}` : ''
  return `/jobs/${described}/describe${only}`
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
  locale: Locale
  // Request-scoped so concurrent requests in one worker can't clobber each other's active locale (React #418).
  i18n: I18n
  // Set ONLY inside a route-transition live preview's shadow router (route-transition/route-preview.tsx): the root and
  // any transition host then render a bare <Outlet/>, so the pane holds the destination route, not a second shell.
  previewFrom?: string
  // Preloads the slot route a `?@modal=` path names (router.tsx wires it to `router.preloadRoute`).
  preloadModal: (path: unknown) => void
}>()({
  validateSearch: rootSearchSchema,
  search: {
    // Stripped AFTER retaining (the outer one runs last), so a retained default never lands in a URL.
    middlewares: [
      stripSearchParams(defaultValues),
      retainSearchParams(['token', 'lang', 'clientId', 'ref', 'chat', 'chatHistory', 'chatSize']),
    ],
  },
  context: () => ({
    sessionUserQueryOptions: trpc.auth.getUser.queryOptions(),
    // Branding changes only through a write, which revalidates it; a tab switch is not a reason to ask again.
    sessionAppQueryOptions: trpc.auth.getApp.queryOptions(undefined, { refetchOnWindowFocus: false }),
  }),
  // A build deployed mid-session: the next navigation goes through the DOCUMENT, so the new build serves it (see
  // lib/app-version). Never for a preload, and never inside a route-transition preview's shadow router.
  beforeLoad: ({ cause, location, context }) => {
    if (cause === 'preload' || context.previewFrom !== undefined) return
    const drift = versionDriftRedirect(location.publicHref)
    if (drift) throw drift
    // A pre-slot dialog link (`?describeJob=5`) is rewritten once to its `?@modal=` path; an older link's `?chat=new`
    // is dropped, as no `?chat=` opens a new conversation.
    const modal = legacyModalPath(location.search)
    const legacyNewChat = (location.search as Record<string, unknown>).chat === 'new'
    if (modal || legacyNewChat) {
      const search: Record<string, unknown> = { ...location.search }
      if (modal) search['@modal'] = modal
      if (legacyNewChat) delete search.chat
      for (const key of LEGACY_MODAL_KEYS) delete search[key]
      // An `href`, not `to` + `search`: `/jobs` and `/offers` retain every param, and would carry the old key along.
      const url = new URL(location.publicHref, 'http://x')
      throw redirect({ href: url.pathname + defaultStringifySearch(search) + url.hash, replace: true })
    }
  },
  // A modal link's intent preload is a preload of the page you are on, and router-core re-runs no loader of a match
  // that STAYS, so without this the root loader below would never see its `?@modal=`. Only the preload lane: the
  // open itself is covered by the outlet, and a blocking reload of the root on a real navigation would pend it.
  shouldReload: ({ preload, location }) => preload && (location.search as Record<string, unknown>)['@modal'] != null,
  loader: ({ context, location }) => {
    // That preload lands here with `?@modal=` in its location: warm the slot route too, so the router's own intent
    // preload covers the dialog's chunk and loader. Client-only: a deep link opens after hydration.
    if (typeof window !== 'undefined') context.preloadModal((location.search as Record<string, unknown>)['@modal'])
    context.queryClient.query(context.sessionUserQueryOptions).catch(noop)
    context.queryClient.query(context.sessionAppQueryOptions).catch(noop)

    // All three are known before the first paint and dehydrated so hydration reads the same value: `theme` from
    // its cookie (no light→dark flash), `radarStyle` from its own because the report's radar is server-rendered
    // (radar-style.ts), and `locale` from the router context, already resolved and activated once in `getRouter`.
    return { theme: resolveServerTheme(), locale: context.locale, radarStyle: resolveRadarStyle() }
  },
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { name: 'theme-color', content: '#33CCAA' },
      { title: 'D’Anchiano' },
      { name: 'description', content: 'La manera más fácil y rápida de evaluar talento' },
    ],
    // app.css and the font preload are deliberately NOT here: a stylesheet emitted via head() gets a `precedence` prop,
    // and React 19 hoists those above our inline `manageTheme` script. A parser-blocking script after a render-blocking
    // stylesheet waits for it, and the browser paints the themeless DOM meanwhile → light→dark flash in system mode.
    // As plain links in JSX they keep DOM order, so the script runs first.
    links: [{ rel: 'shortcut icon', href: assetUrl('favicon.ico') }],
    // A separate entry, and a plain constant: it arms a MutationObserver that must run before the parser delivers the
    // body, so it can't wait for the deferred entry module. Its text must be byte-identical on both sides or the whole
    // head bootstrap re-executes on hydration.
    scripts: [
      { children: initGlobalsScript },
      { children: traceInitScript(ssrTrace()) },
      { children: HYDRATION_REWRITE_MONITOR_SCRIPT },
    ],
  }),
  component: RootComponent,
  // Renders INSIDE RootComponent's already-mounted shell, so it must emit only the not-found content: its own
  // <RootDocument> nested a second <html>/<head>/<body>, which browsers strip while parsing → React #418. See #47.
  notFoundComponent: () => {
    return <p>{'Not found :('}</p>
  },
})

// Dev-only pointer-inspection overlay. A module-level helper, not an inline import() in the effect: the React
// Compiler bails out of a whole component containing an import(). Off under VITE_E2E, where the overlay would
// intercept pointer events on full-page views and inside portals.
function loadReactGrabDevOverlay() {
  if (import.meta.env.DEV && !import.meta.env.VITE_E2E) {
    void import('react-grab')
  }
}

function RootComponent() {
  const { theme, locale } = Route.useLoaderData()
  // Inside a route-transition live preview the document shell already exists in the real tree the preview renders
  // inside, so redrawing it would nest a second document in the pane. See route-transition/route-preview.tsx.
  const { previewFrom } = Route.useRouteContext()
  // A light-only page renders without the `dark` class even when the cookie asks for it (see light-only-routes.ts).
  const lightOnly = useRouterState({ select: (state) => isLightOnlyPath(state.location.pathname) })
  const router = useRouter()
  if (import.meta.env.SSR) preloadRouteCatalogs(router, locale)

  React.useEffect(() => {
    loadReactGrabDevOverlay()
  }, [])

  React.useEffect(() => trackPageScrollbar(), [])

  // Stamped in an effect, so it appears only once the client tree has committed and React's root event delegation is
  // live — until then a click on streamed SSR markup is silently dropped. The e2e harness gates navigation on it.
  React.useEffect(() => {
    document.documentElement.dataset.hydrated = 'true'
  }, [])

  if (previewFrom !== undefined) return <Outlet />

  return (
    <RootDocument theme={lightOnly ? 'light' : theme} locale={locale}>
      <AppShell>
        <Outlet />
      </AppShell>
    </RootDocument>
  )
}

function RootDocument(
  props: Readonly<{ children: React.ReactNode; theme?: 'dark' | 'light' | null; locale?: string }>,
) {
  // Resolved per render so SSR (process.env.URL_CDN) and the client's first render (window.__CDN_URL) agree byte-for-byte.
  const cdnUrl = getRuntimeCdnUrl()
  const cdnOrigin = cdnUrl.startsWith('http') ? new URL(cdnUrl).origin : ''
  const fontUrl = assetUrl('plus-jakarta-sans-2.071-latin-400-700.woff2')
  // Inline beside the preload, NOT in app.css, so the browser matches the @font-face to the preloaded woff2 at
  // initial parse — otherwise Chrome warns the preload is unused and text falls back to metric-matched Arial. The
  // dev server must let that response be REUSED too (`cacheableDevFonts`, vite-config.md#cacheable-dev-fonts). The
  // file carries only the 400-700 range (scripts/build-font-subset.py), so declaring it clamps anything outside.
  const fontFaceStyle = `@font-face{font-family:'Plus Jakarta Sans';font-style:normal;font-display:swap;font-weight:400 700;src:url('${fontUrl}') format('woff2')}`
  return (
    // In system mode `theme` is null (the server can't read the OS preference) and the inline manageTheme script adds
    // `dark` before paint; suppressHydrationWarning lets that script-set class stand through hydration. The script and
    // the AppShell effect stay the live source of truth for system mode, OS changes and toggles.
    <html lang={props.locale} className={props.theme === 'dark' ? 'dark' : undefined} suppressHydrationWarning>
      <head>
        {/* Warm up the cross-origin CDN connection (DNS+TCP+TLS) before the parser reaches the asset
            URLs, so the handshake overlaps HTML parsing instead of blocking the first chunk/font.
            crossOrigin matches the anonymous CORS fetches the CDN serves (modules + font). Skipped
            when assets are same-origin (dev), where CDN_ORIGIN is ''. */}
        {cdnOrigin ? (
          <>
            <link rel="preconnect" href={cdnOrigin} crossOrigin="anonymous" />
            <link rel="dns-prefetch" href={cdnOrigin} />
          </>
        ) : null}
        {/* manageTheme MUST run before the stylesheet. It sets the `dark` class from the cookie /
            `prefers-color-scheme` so the first paint is correct even in system mode (no `theme`
            cookie → the server can't pre-paint the class on <html>). */}
        <script dangerouslySetInnerHTML={{ __html: manageThemeScript }} />
        {/* Plain (non-precedence) <link> so React 19 keeps it HERE in DOM order — after the script
            — instead of hoisting it above (which it does for precedence stylesheets emitted via
            head().links). Still render-blocking, so no flash of unstyled content. See head() note. */}
        <link rel="stylesheet" href={appCss} />
        <link rel="preload" href={fontUrl} as="font" type="font/woff2" crossOrigin="anonymous" />
        <style dangerouslySetInnerHTML={{ __html: fontFaceStyle }} />
        <style dangerouslySetInnerHTML={{ __html: SDK_EMBED_STYLE }} />
        <HeadContent />
      </head>
      <body>
        {props.children}
        <Scripts />
      </body>
    </html>
  )
}

// Without this, FieldLabelRow falls back to its hardcoded English "Optional" in every locale.
function OptionalLabelProvider(props: Readonly<{ children: React.ReactNode }>) {
  const { t } = useLingui()
  return <FormOptionalLabelProvider value={t`Optional`}>{props.children}</FormOptionalLabelProvider>
}

// The API's `darkMode: true | false | null` (null = follow the OS) as the store's mode.
const themeMode = (darkMode: boolean | null) => (darkMode === null ? 'system' : darkMode ? 'dark' : 'light')

// The config comes from its OWN `getApp` query, keyed on the request's appId rather than the user, so an embed/API
// visitor gets the client's branding even when logged out. `app` is undefined outside an app/embed context.
function AppConfigSync() {
  const { data: app } = useSuspenseQuery(Route.useRouteContext().sessionAppQueryOptions)
  const setStore = useAppStore((state) => state.setStore)

  React.useEffect(() => {
    if (!app) return
    // In app/SDK context the client's config sets the mode (`null` is its "auto": follow the OS), unless the host already
    // passed `customize.darkMode` to the SDK render — that live override wins whichever of the two arrives first.
    const sdkOverride = useAppStore.getState().store.customize.darkMode !== undefined
    setStore({
      app,
      ...(sdkOverride ? {} : { darkMode: themeMode(app.darkMode) }),
      customize: {
        accentColor: app.accentColor ?? null,
        applicantButtonExit: app.applicantButtonExit ?? undefined,
        jobButtonExit: app.jobButtonExit ?? undefined,
        enableJobAddApplicantButton: app.enableJobAddApplicantButton,
      },
    })
  }, [app, setStore])

  return null
}

function AppUserSync() {
  const user = useSessionUser()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const routeTo = useAppStore((state) => state.store.routeTo)
  const profiles = useAppStore((state) => state.store.profiles)

  // Identity guard for the persisted cache — docs/notes/app/query-persistence.md, "whose blob is this?".
  React.useEffect(() => {
    if (!OWNS_PERSISTED_CACHE) return
    const currentId = persistedCacheIdentity(user)
    if (adoptCacheIdentity(currentId)) {
      clearQueryClient()
      adoptCacheIdentity(currentId)
      queryClient.setQueryData(trpc.auth.getUser.queryKey(), user)
    }
    window.localStorage.setItem(PERSISTED_IDENTITY_KEY, currentId)
  }, [user, queryClient])

  React.useEffect(() => {
    if (routeTo) {
      // TODO: maintaining retrocompatibility with old sdk
      // TODO: Fix this, surely is not working correctly now with the new router
      navigate({
        to: routeTo,
        search: {
          ...profiles,
          second:
            profiles?.first && !profiles.second && user?.type === 'applicant'
              ? { type: 'applicant', id: user.id }
              : profiles?.second,
        },
        replace: true,
      })
    }
  }, [user, routeTo, profiles, navigate])

  return null
}

/**
 * An open dialog in an SDK embed, as the frame is sized to it: the surfaces themselves, or the skeleton the modal slot
 * paints before a dialog's code arrives. A closing one (`data-closed`) no longer counts, so the frame goes back to the
 * page while it fades.
 */
const EMBED_DIALOG_SURFACE =
  ':is([data-slot=dialog-content],[data-slot=alert-dialog-content],[data-slot=dialog-skeleton])'
const EMBED_DIALOG_SELECTOR = `${EMBED_DIALOG_SURFACE}[data-open]`
/** The gutter above and below a centred card, as its `max-w-[calc(100%-2rem)]` keeps at the sides. */
const EMBED_DIALOG_GUTTER = 16

/**
 * The height an embed needs to show its open dialog in full, or null with none open. The dialog is centred in the
 * viewport, so a frame sized to the page under it (a whole report behind the describe wizard) showed a card lost in
 * a tall page. `offsetHeight`, not the rect: the enter animation scales the popup to 95%.
 */
function embeddedDialogHeight(): number | null {
  let height = 0
  for (const dialog of document.querySelectorAll<HTMLElement>(EMBED_DIALOG_SELECTOR)) {
    // A dialog that is full-screen on a phone (the describe wizard) spans the width and keeps no gutter.
    const gutter = dialog.offsetWidth >= document.documentElement.clientWidth ? 0 : EMBED_DIALOG_GUTTER
    height = Math.max(height, dialog.offsetHeight + 2 * gutter)
  }
  return height === 0 ? null : Math.ceil(height)
}

/**
 * Inside an SDK iframe the host sizes the frame to the height we report, so what we report must not depend on the
 * frame. The shell is viewport-locked (`h-dvh` with a scrolling pane on a phone, `min-h-dvh` on desktop), and
 * reporting `#root-page` fed the frame's own height back, minus any border: the embed shrank step by step. The
 * desktop minimum is dropped here; the phone lock stays, since the stack's slots need a definite height, and
 * `embeddedContentHeight` measures past it. Unlayered, so it outranks the Tailwind utilities.
 */
const SDK_EMBED_STYLE =
  `html[data-sdk-embed] body,html[data-sdk-embed] [data-app-layout]{min-height:0}` +
  // An open dialog is what the frame is sized to (`embeddedDialogHeight`), so its own height must not come from the
  // frame either: a viewport floor (the describe wizard's phone `min-h-dvh`) would feed the report back.
  `html[data-sdk-embed] ${EMBED_DIALOG_SURFACE}{height:auto;min-height:0;max-height:none}`

/** `#root-page` as if no pane scrolled: each scrolling pane's viewport swapped for the height of what it holds. */
function embeddedContentHeight(root: HTMLElement): number {
  let height = root.getBoundingClientRect().height
  for (const pane of root.querySelectorAll('.scrollable-area')) {
    if (pane.clientHeight === 0 || !/auto|scroll/.test(getComputedStyle(pane).overflowY)) continue
    const top = pane.getBoundingClientRect().top - pane.scrollTop
    let bottom = 0
    for (const child of pane.children) bottom = Math.max(bottom, child.getBoundingClientRect().bottom - top)
    height += bottom - pane.clientHeight
  }
  // Ceiled: a fractional height rounded down on the way back would read as a 1px shrink every round.
  return Math.ceil(height)
}

// A rendered <style>, not a post-mount effect: the per-app color comes from the SSR-prefetched `getApp`, so the rule
// is in the server HTML and the accent paints with the first frame — no teal→custom flash, and it paints for a
// logged-out embed visitor too. A live SDK `customize` postMessage overrides it via the store.
// `:root:root` wins over app.css's `:root`/`.dark`, and it is emitted after the app.css <link> for equal-specificity ties.
function AccentTheme() {
  const { data: app } = useSuspenseQuery(Route.useRouteContext().sessionAppQueryOptions)
  const overrideAccent = useAppStore((state) => state.store.customize.accentColor)
  const accent = overrideAccent ?? app?.accentColor ?? null
  const accentVars = Object.entries(accentThemeVars(accent))
    .map(([key, value]) => `${key}:${value}`)
    .join(';')
  const css = `:root:root{${accentVars}}`
  return <style>{css}</style>
}

function AppShell(props: Readonly<{ children: React.ReactNode }>) {
  const queryClient = useQueryClient()
  // Request-scoped instance from router context — never the shared global — so concurrent SSR renders can't clobber
  // this request's locale (React #418).
  const { i18n } = Route.useRouteContext()
  /**
   * SDK
   * - run renders and requests from parent
   * - send response for requests made from parent
   * - recover last render props after app autoupdate (see Page props implementation)
   */
  const sdk = typeof window !== 'undefined' && window.self !== window.top
  const setStore = useAppStore((state) => state.setStore)
  const clientId = useAppStore((state) => state.store.clientId)
  const locale = useAppStore((state) => state.store.locale)
  const darkMode = useAppStore((state) => state.store.darkMode)
  const app = useAppStore((state) => state.store.app)

  React.useEffect(() => {
    const listener = (
      event: MessageEvent<{
        type: string
        props?: Record<string, unknown>
        url?: string
        params?: unknown
        key?: string
      }>,
    ) => {
      if (event.data.type === 'danchiano_render') {
        const sdkDarkMode = (event.data.props?.customize as Partial<Customize> | undefined)?.darkMode
        setStore({
          ...event.data.props,
          ...(sdkDarkMode === undefined ? {} : { darkMode: themeMode(sdkDarkMode) }),
          sdk,
          callback: (value) => {
            if (!sdk) return
            window.parent.postMessage({ type: 'danchiano_callback', result: value }, '*')
          },
        })
      } else if (event.data.type === 'danchiano_request') {
        if (!event.data.url || !event.data.params) return

        // TODO: check if we need all of this flow, we should add headers and everything here
        fetch(event.data.url, event.data.params)
          .then((result) => {
            window.parent.postMessage(
              {
                type: 'danchiano_response',
                key: event.data.key,
                result,
              },
              '*',
            )
          })
          .catch((error: unknown) => {
            window.parent.postMessage(
              {
                type: 'danchiano_response',
                key: event.data.key,
                result: (error as { response?: string }).response ?? error,
              },
              '*',
            )
          })
      }
    }

    window.addEventListener('message', listener, false)
    window.parent.postMessage({ type: 'danchiano_loaded' }, '*')
    return () => window.removeEventListener('message', listener, false)
  }, [sdk, setStore])

  React.useEffect(() => {
    if (!sdk) return
    const root = document.getElementById('root-page')
    if (!root) return

    // Drops the desktop `min-h-dvh` (SDK_EMBED_STYLE) BEFORE the first measurement.
    document.documentElement.dataset.sdkEmbed = ''
    let sent = 0
    let width = window.innerWidth
    // Set only by a change IN this document (DOM, a load, an animation, the width) and never by the frame height the
    // host sets from our report, so a report can never cause the next one: content sized from the viewport at some
    // depth would otherwise grow the frame on every round trip, until the renderer runs out of memory.
    let dirty = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const sendHeight = () => {
      timer = undefined
      if (!dirty) return
      dirty = false
      const height = embeddedDialogHeight() ?? embeddedContentHeight(root)
      // 0 is a frame hidden by its host (a tab it switched away from): nothing to report, and keeping the last
      // height means the embed comes back at its size.
      if (height === 0 || height === sent) return
      sent = height
      window.parent.postMessage({ type: 'danchiano_resize', height: `${height}px` }, '*')
    }
    // A timer, not rAF: rAF never fires while the host tab is hidden, so an embed opened in the background kept its
    // placeholder height until the viewer came back.
    const schedule = () => {
      if (timer === undefined) timer = setTimeout(sendHeight, 0)
    }
    const changed = () => {
      dirty = true
      schedule()
    }
    // A pane's content grows without resizing `#root-page`, so its children are observed too, and re-collected when
    // a navigation swaps the pane. A resize alone counts only when the width moved.
    const resize = new ResizeObserver(() => {
      if (window.innerWidth !== width) {
        width = window.innerWidth
        dirty = true
      }
      schedule()
    })
    const observe = () => {
      resize.disconnect()
      resize.observe(root)
      for (const pane of root.querySelectorAll('.scrollable-area'))
        for (const child of pane.children) resize.observe(child)
      changed()
    }
    const mutations = new MutationObserver((records) =>
      records.some((record) => record.type === 'childList') ? observe() : changed(),
    )
    // The body, not `#root-page`: a dialog is portalled beside it, and opening, closing or stepping through one moves
    // the height.
    mutations.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true })
    const settled = ['load', 'transitionend', 'animationend'] as const
    for (const type of settled) document.addEventListener(type, changed, true)
    document.fonts.addEventListener('loadingdone', changed)
    observe()
    return () => {
      resize.disconnect()
      mutations.disconnect()
      for (const type of settled) document.removeEventListener(type, changed, true)
      document.fonts.removeEventListener('loadingdone', changed)
      clearTimeout(timer)
      delete document.documentElement.dataset.sdkEmbed
    }
  }, [sdk])

  const setHydrated = useAppStore((state) => state.setHydrated)
  React.useEffect(() => {
    // SSR'd interactive controls read this (via useHydrated) to stay disabled until handlers are attached, so
    // pre-hydration clicks aren't silently dropped.
    setHydrated()
  }, [setHydrated])

  React.useEffect(() => {
    // Keeps assistive tech announcing content in the right language; the SSR value comes from the loader.
    if (locale) document.documentElement.lang = locale
    // A visitor who arrived on a locale-prefixed public URL chose that language by opening it, so it has to
    // survive the first navigation into the app, where no path carries it (lib/locale-path.ts).
    if (locale && splitLocalePath(window.location.pathname).locale) setCookie('language', locale)
  }, [locale])

  const prevScope = React.useRef<string | null>(null)
  React.useEffect(() => {
    // Only on an actual VALUE change: this effect re-runs with the same clientId/locale (e.g. after getUser resolves),
    // and invalidating then throws away the fresh SSR-hydrated cache, so a "skip first run" flag isn't enough.
    const scope = `${clientId ?? ''}|${locale ?? ''}`
    if (prevScope.current !== null && prevScope.current !== scope) {
      queryClient.invalidateQueries()
    }
    prevScope.current = scope
  }, [clientId, locale, queryClient])

  // The visitor's `theme` cookie is their PERSONAL standalone preference. In any embed/API context — the SDK iframe,
  // or an API token-link app (full page, so `sdk` is false but `app` is set) — the client's config dictates the mode.
  const isEmbedded = sdk || !!app
  const lightOnly = useRouterState({ select: (state) => isLightOnlyPath(state.location.pathname) })

  React.useEffect(() => {
    // Read by manageThemeScript's OS-change listener: a pinned mode (an SDK `customize.darkMode`, an app config, a
    // light-only page) must survive the OS flipping its scheme — an embed carries no `theme` cookie to say so.
    document.documentElement.setAttribute('data-theme-mode', lightOnly ? 'light' : darkMode)
    // A light-only page (the new landing) stays light without touching the saved preference, which applies again as
    // soon as the visitor navigates anywhere else (this effect re-runs on the path change).
    if (lightOnly) {
      document.documentElement.classList.remove('dark')
      setStore({ resolvedDarkMode: 'light' })
      return
    }
    if (darkMode === 'dark') {
      if (!isEmbedded) setCookie('theme', 'dark')

      document.documentElement.classList.add('dark')
      setStore({ resolvedDarkMode: 'dark' })
    } else if (darkMode === 'light') {
      if (!isEmbedded) setCookie('theme', 'light')

      document.documentElement.classList.remove('dark')
      setStore({ resolvedDarkMode: 'light' })
    } else {
      if (!isEmbedded) deleteCookie('theme')

      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark')
        setStore({ resolvedDarkMode: 'dark' })
      } else {
        document.documentElement.classList.remove('dark')
        setStore({ resolvedDarkMode: 'light' })
      }
    }
  }, [darkMode, isEmbedded, lightOnly, setStore])

  // Deliberately never auto-reloads: mid-deployment the flag flaps between old and new servers, which would reload
  // the page repeatedly under the user.

  return (
    <div id="root-page" className="relative mx-auto">
      <I18nProvider i18n={i18n}>
        <TooltipProvider delay={30}>
          <OptionalLabelProvider>
            <React.Suspense fallback={null}>
              <AppConfigSync />
              <AppUserSync />
              <AccentTheme />
            </React.Suspense>
            {props.children}
            <ClientOnly fallback={null}>
              <CookieBanner />
            </ClientOnly>
            {/* The one modal slot: every dialog route renders here, over whichever page is matched. */}
            <ModalSlotOutlet />
          </OptionalLabelProvider>
        </TooltipProvider>
        <AppToaster />
      </I18nProvider>

      {/* Off under VITE_E2E: its server vite plugin (the console-pipe) is disabled in
          the e2e stack, so the panel's /__tsd/* calls would 404; and it's pure dev UI
          irrelevant to headless tests. */}
      {import.meta.env.DEV && !import.meta.env.VITE_E2E && (
        <ClientOnly fallback={null}>
          <React.Suspense fallback={null}>
            <DevtoolsPanels />
          </React.Suspense>
        </ClientOnly>
      )}
    </div>
  )
}

/**
 * The devtools, reached only through `import()`.
 *
 * The `import.meta.env.DEV` guard at the call site removes the ELEMENTS from a production build, but a
 * top-level `import` of these packages still makes them statically reachable, and
 * `@tanstack/devtools-event-client`'s module-scope side effects survived the shake — 7.6 KB minified of
 * pure dev tooling on the chunk every visitor loads before hydration. A dynamic import has no such edge.
 */
const DevtoolsPanels = React.lazy(async () => {
  const [{ TanStackDevtools }, { TanStackRouterDevtoolsPanel }, { ReactQueryDevtoolsPanel }] = await Promise.all([
    import('@tanstack/react-devtools'),
    import('@tanstack/react-router-devtools'),
    import('@tanstack/react-query-devtools'),
  ])
  return {
    default: () => (
      <TanStackDevtools
        plugins={[
          { name: 'Tanstack Router', render: <TanStackRouterDevtoolsPanel /> },
          { name: 'Tanstack Query', render: <ReactQueryDevtoolsPanel /> },
        ]}
      />
    ),
  }
})
